import React from "react";
import PropTypes from "prop-types";
import { FullscreenModal } from "@learningblocks/storybook-components/lib/FullscreenModal";
import { Image } from "@learningblocks/shared-components/lib/Image";
import { TopViewContent } from "./TopViewContent";
import { FrontViewContent } from "./FrontViewContent";
import { TABS } from "./ModalWindow.utils";
import { Player } from "./Player";
import { MCStateContext } from "..";
import { useTranslation } from "react-i18next";
import { LidOpen } from "./TopViewContent/LidOpen";
import { LidClosed } from "./TopViewContent/LidClosed";
import { Settings } from "./FrontViewContent/Settings";
import MCFrontView from "@common/images/mc_frontview.svg";
import MCFrontViewOpen from "@common/images/mc_frontview_open.svg";

import cname from "./style.module.scss";

const TRANSLATION_KEY = {
  TOP_VIEW: "TopView.string",
  FRONT_VIEW: "FrontView.string",
  FRONT_VIEW_TITLE: "FrontViewTitle.string",
  TOP_VIEW_TITLE: "TopViewTitle.string",
};

export function ModalWindow(props) {
  const { running, pulsing, isLidOpen } = React.useContext(MCStateContext);

  const { t } = useTranslation();

  const onClickTab = (tab) => {
    props.setCurrentTab(tab);
  };

  const showAnimation = () => running && !pulsing;

  const renderTopViewEquipment = () =>
    isLidOpen ? (
      <LidOpen
        toggleLid={props.toggleLid}
        onDrop={props.onDrop}
        slots={props.slots}
        balanced={props.balanced}
      />
    ) : (
      <LidClosed toggleLid={props.toggleLid} />
    );

  const renderTabHeading = () => (
    <div className={cname.tabList}>
      <button
        className={`${cname.tabBtn} ${props.currentTab === TABS.TOP_VIEW ? cname.tabBtnActive : ""}`}
        onClick={() => onClickTab(TABS.TOP_VIEW)}
        tabIndex={0}
      >
        <div className={cname.tabLabel}>{t(TRANSLATION_KEY.TOP_VIEW)}</div>
        <div className={cname.tabTitle}>
          1. {t(TRANSLATION_KEY.TOP_VIEW_TITLE)}
        </div>
      </button>
      <button
        className={`${cname.tabBtn} ${props.currentTab === TABS.FRONT_VIEW ? cname.tabBtnActive : ""}`}
        onClick={() => onClickTab(TABS.FRONT_VIEW)}
        tabIndex={0}
      >
        <div className={cname.tabLabel}>{t(TRANSLATION_KEY.FRONT_VIEW)}</div>
        <div className={cname.tabTitle}>
          2. {t(TRANSLATION_KEY.FRONT_VIEW_TITLE)}
        </div>
      </button>
    </div>
  );

  return (
    <FullscreenModal
      className={cname.mc_modal}
      closeButton={true}
      close={props.closeModal}
      isOpen={props.isOpen}
      notebookExpanded={props.notebookExpanded}
      lang={props.lang}
    >
      <div className={cname.sections}>
        <div className={cname.panelWrapper}>
          {renderTabHeading()}
          <div className={cname.panel}>
            {props.currentTab === TABS.TOP_VIEW ? (
              <TopViewContent
                isLidOpen={isLidOpen}
                toggleLid={props.toggleLid}
                onDrop={props.onDrop}
                slots={props.slots}
                balanced={props.balanced}
              />
            ) : (
              <FrontViewContent
                data={props.frontViewData}
                setRPM={props.setRPM}
                setTimer={props.setTimer}
                onRunCentrifuge={props.onRunCentrifuge}
                onStopCentrifuge={props.onStopCentrifuge}
              />
            )}
          </div>
        </div>
        <div className={cname.graphicWrapper}>
          {props.currentTab === TABS.TOP_VIEW ? (
            renderTopViewEquipment()
          ) : (
            <div className={cname.frontView}>
              <Image
                className={cname.frontViewImage}
                src={isLidOpen ? MCFrontViewOpen : MCFrontView}
                alt=""
              />
              <Settings
                data={props.frontViewData.settings}
                setRPM={props.setRPM}
                setTimer={props.setTimer}
                onRunCentrifuge={props.onRunCentrifuge}
                onStopCentrifuge={props.onStopCentrifuge}
              />
            </div>
          )}
        </div>
        {showAnimation() && (
          <div className={cname.timerWrapper}>
            <Player player={props.player} />
          </div>
        )}
      </div>
    </FullscreenModal>
  );
}

ModalWindow.PropTypes = {
  /**
   * tells if modal window is open
   */
  isOpen: PropTypes.bool,
  ...Player.propTypes,
  ...FrontViewContent.propTypes,
  ...TopViewContent.propTypes,
};
